import { mapHomeRoleToTourRole, TOUR_ROLES } from "./tourIds";

function storageKey(userId) {
  return `pingflo:uiTour:${userId || "anon"}`;
}

function dismissKey(userId, role) {
  const tourRole = mapHomeRoleToTourRole(role);
  return `pingflo:uiTourDismissed:${userId || "anon"}:${tourRole}`;
}

function currentLoginTokenStamp() {
  try {
    const token = String(localStorage.getItem("token") || "");
    if (!token) return "";
    return token.length <= 24 ? token : token.slice(-24);
  } catch {
    return "";
  }
}

export function readUiTourLocal(userId) {
  try {
    const raw = JSON.parse(localStorage.getItem(storageKey(userId)) || "null");
    return raw && typeof raw === "object" ? raw : {};
  } catch {
    return {};
  }
}

export function writeUiTourLocal(userId, next) {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(next || {}));
  } catch {
    /* ignore */
  }
}

/** True only after the user finished the last guided-tour step. */
export function isRoleTourCompleted(record) {
  if (!record || typeof record !== "object") return false;
  return Boolean(record.completedAt);
}

/**
 * @deprecated Prefer isRoleTourCompleted for auto-welcome.
 * Kept for callers that treated skip as settled; now matches completed-only.
 */
export function isRoleTourSettled(record) {
  return isRoleTourCompleted(record);
}

/** Hide the welcome for the rest of this login while the choice is being saved. */
export function markUiTourDismissedThisLogin(userId, role) {
  try {
    const stamp = currentLoginTokenStamp();
    if (!stamp) return;
    sessionStorage.setItem(dismissKey(userId, role), stamp);
  } catch {
    /* ignore */
  }
}

export function isUiTourDismissedThisLogin(userId, role) {
  try {
    const stamp = currentLoginTokenStamp();
    if (!stamp) return false;
    return sessionStorage.getItem(dismissKey(userId, role)) === stamp;
  } catch {
    return false;
  }
}

/**
 * Digiping unlocks after the tour is completed, or after the user chooses
 * to continue later. The manual stays available on demand.
 */
export function isUiTourSettledForRole(homeRole, userId) {
  try {
    const brand = JSON.parse(localStorage.getItem("brand_config") || "{}");
    if (brand.show_ai_coach === false) return true;
  } catch {
    /* ignore */
  }
  const role = mapHomeRoleToTourRole(homeRole);
  const uid =
    userId ||
    (() => {
      try {
        return JSON.parse(localStorage.getItem("user") || "null")?.user_id;
      } catch {
        return null;
      }
    })();
  const local = readUiTourLocal(uid);
  if (isRoleTourCompleted(local[role])) return true;
  if (local[role]?.continueLaterAt || local[role]?.lastDismissedAt) return true;
  return isUiTourDismissedThisLogin(uid, role);
}

/** First visit only, until the user finishes the tour or chooses to continue later. */
export function shouldAutoShowUiTourWelcome(homeRole, userId) {
  const role = mapHomeRoleToTourRole(homeRole);
  const uid =
    userId ||
    (() => {
      try {
        return JSON.parse(localStorage.getItem("user") || "null")?.user_id;
      } catch {
        return null;
      }
    })();
  const local = readUiTourLocal(uid);
  const record = local[role] || {};
  if (isRoleTourCompleted(record)) return false;
  if (record.continueLaterAt || record.lastDismissedAt) return false;
  if (isUiTourDismissedThisLogin(uid, role)) return false;
  return true;
}

export function mergeUiTourState(current, role, patch) {
  const next = { ...(current || {}) };
  for (const r of TOUR_ROLES) {
    next[r] = { ...(next[r] || {}) };
  }
  const key = mapHomeRoleToTourRole(role);
  next[key] = { ...(next[key] || {}), ...patch };
  return next;
}
